import { DocumentManagerService } from '../document/document-manager.service';
import { ChainSenders, ValidatorsArgs } from './chain.interface';
import { CustomTextareaService } from '../text-notes/custom-textarea.service';
import { ValidationEvent } from './validation-event';
import { AsgardFeatureNames } from 'src/models/AsgardFeatures';
import { PaneService } from '../pane.service';
import { NgZone, inject } from '@angular/core';


export class BaseChainValidator {
  private commonValidators: ValidationEvent[] = [];
  private uiValidators: ValidationEvent[] = [];
  private pdfValidators: ValidationEvent[] = [];
  private htmlValidators: ValidationEvent[] = [];

  private readonly docMgrService: DocumentManagerService = inject(DocumentManagerService);
  private readonly area: CustomTextareaService = inject(CustomTextareaService);
  private readonly paneService: PaneService = inject(PaneService);
  private readonly ngZone: NgZone = inject(NgZone);

  private validators(sender: ChainSenders | null): ValidationEvent[] {
    if (sender) {
      switch (sender) {
        case ChainSenders.UI:
          return this.uiValidators;
        case ChainSenders.PDF_DOCUMENT:
          return this.pdfValidators;
        case ChainSenders.HTML_DOCUMENT:
          return this.htmlValidators;
        default:
          return this.commonValidators;

      }
    } else {
      return this.commonValidators;
    }
  }

  constructor() {
    this.init();
  }

  public async validate(sender: ChainSenders,
    feature: AsgardFeatureNames | null = null,
    documentId: string | null = null,
    paneIndex: number | null = null, event: any, args: any): Promise<boolean> {

    const params: ValidatorsArgs = {
      feature,
      documentId,
      paneIndex,
      args,
      event
    };
    // console.log('validate', params);
    // first check common validators
    const commonValidators = await Promise.all(this.validators(null).map(e => {
      if (e.required) {
        return e.raise(params);
      } else {
        return false;
      }
    }));
    const commonValidatorsChecked = this.proceedToHandler(commonValidators);
    if (!commonValidatorsChecked) {
      return false;
    }

    /* then validate by sender */
    const result = await Promise.all(this.validators(sender).map(e => {
      if (e.required) {
        return e.raise(params);
      } else {
        return false;
      }
    }));
    return this.proceedToHandler(result);
  }

  private proceedToHandler(result: boolean[]): boolean {
    return !(result.length && result.indexOf(true) !== -1);
  }

  private init(): void {
    /* Common validators for every click */
    this.add(null, new ValidationEvent(this.isTextNoteOpenedValidator(), true));
    this.add(null, new ValidationEvent(this.paneIndexValidation(), true));
  }

  private add(sender: ChainSenders | null = null, validator: ValidationEvent): void {
    if (sender) {
      switch (sender) {
        case ChainSenders.UI:
          this.uiValidators.push(validator);
          break;
        case ChainSenders.HTML_DOCUMENT:
          this.htmlValidators.push(validator);
          break;
        case ChainSenders.PDF_DOCUMENT:
          this.pdfValidators.push(validator);
          break;
        default: break;
      }
    } else {
      this.commonValidators.push(validator);
    }
  }

  private isTextNoteOpenedValidator = (): (p: ValidatorsArgs) => Promise<boolean> => {
    return async (params: ValidatorsArgs) => {
      const textNoteDocumentId = this.area.docId;

      // when text note clicked by it self - do not close it
      if (params.documentId && params.documentId === textNoteDocumentId) {
        // console.warn('textNoteClicked:', true);
        return false;
      } else {
        // when clicked one of the features in list that shouldnt close the text note
        const exceptions = [AsgardFeatureNames.TEXT_NOTE_STYLE, AsgardFeatureNames.AUDIO_MAKER, AsgardFeatureNames.SPELL_CHECK, AsgardFeatureNames.WORD_PREDICTION, AsgardFeatureNames.SPEECH_RECOGNITION];
        if (params.feature) {
          const exceptionsClicked = exceptions.indexOf(params.feature) !== -1;
          // return false means -> procceed to original click handler
          // console.log('exceptionsClicked:', exceptionsClicked);
          if (exceptionsClicked) {
            return false;
          }
        }
        // if text note is open - close it,
        // excpet cases above
        if (this.docMgrService.currentDoc && this.docMgrService.documentReady && this.docMgrService.isFocusDocument
          && this.docMgrService.currentDoc.documentId === textNoteDocumentId) {
          await this.area.removeFocusDoc();
        }
      }
      return false;
    };
  }


  private paneIndexValidation = () => {
    return (params: ValidatorsArgs) => {
      return this.ngZone.run(() => {
        if (params.paneIndex || params.paneIndex === 0) {
          this.paneService.setActivePaneIndex(params.paneIndex);
        }
        return false;
      });
    };
  }

}
